import { Divider, Box } from '@mui/material';
import React, { ReactNode } from 'react'
import styles from './DetailsView.module.css';
import { formatUTCDateTime } from '../../../utils/formatUTCDate';
import copyToClipboard from '../../../utils/copyToClipboard';
import { toast } from 'react-toastify';
import CopyLinkIcon from '../../../Components/Icons/copyLink';
import { ApplicationDisplayItem } from '../../../Models/applicationModels';
import { LinkShowcase } from './components/linkShowcase/linkShowcase';
import { ADDRESS_STATE, CONVERSATION_ID, FLAG_PAYMENT_FAILED, LEAD_STATE_ABBR, OPPORTUNITY_UID, PIVOT_SOURCE_AT, PIVOT_SOURCE_CONVERSATION_UID, PIVOT_SOURCE_OPPORTUNITY_UID, PIVOT_SOURCE_REASON, PIVOT_SOURCE_FROM, PIVOT_TARGET_AT, PIVOT_TARGET_CONVERSATION_UID, PIVOT_TARGET_OPPORTUNITY_UID, PIVOT_TARGET_TO, APPLICATION_DETAILS_LABEL_MAP, LAST_EVENT, FIRST_NAME, LAST_NAME, AGENT_NAME, AGENT_ID } from '../../../utils/constants/applicationDetailsBifurcation';
import { HIGHLIGHT_COLOR } from './detailsView.constants';
import { DetailsItemCard } from './components/detailsItemCard/detailsItemCard.index';
import { useInsightsUtils } from '../../../hooks/useInsightsUtils';
import { AGENTS_EDIT_ROUTE, EDIT_AGENT_QUERY_PARAM } from '../../agents/agents.constants';
import { Link } from 'react-router-dom';

const HIDDEN_KEYS = new Set([PIVOT_SOURCE_AT, PIVOT_TARGET_AT]);

const handleCopyLink = (value: string, type: string) => {
    if (!value) return;
    copyToClipboard(value, () => {
        toast.success(`${type} copied to clipboard`);
    });
}

const renderUid = (value: string, type: string) => {
    return <div className={styles.linkDiv}>
        {value}
        {value && <CopyLinkIcon className={styles.copyLinkIcon} onClick={() => handleCopyLink(value, type)} />}
    </div>;
}

/**
 * Default renderer for detail values. If the value is an ISO 8601 date-time string,
 * format it for display; otherwise return the string unchanged (e.g. names, details, etc).
 */
const renderDefault = (value: string) => {
    const iso8601Regex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/;
    const stringValue = String(value).trim();
    if (iso8601Regex.test(stringValue)) {
        return formatUTCDateTime(stringValue, false, true)
    } else {
        return stringValue
    }
}


export const DetailsView = ({
    title,
    items,
    noDivider = false,
    identityContext,
}: {
    title: string;
    items: ApplicationDisplayItem[];
    noDivider?: boolean;
    identityContext?: {
        opportunityId?: string;
        firstName?: string;
        lastName?: string;
    };
}) => {
    const { HandleLastEventClick } = useInsightsUtils();
    if (!items?.length) return null;
    const userId = items.find((item) => item.key === AGENT_ID)?.value;
    const pivotSourceOpportunityUid = items.find((item) => item.key === PIVOT_SOURCE_OPPORTUNITY_UID)?.value;
    const pivotTargetOpportunityUid = items.find((item) => item.key === PIVOT_TARGET_OPPORTUNITY_UID)?.value;
    const pivotIsSourceGroupAvailable = Boolean(pivotSourceOpportunityUid);
    const pivotIsTargetGroupAvailable = Boolean(pivotTargetOpportunityUid);
    const firstName = identityContext?.firstName ?? items.find((item) => item.key === FIRST_NAME)?.value;
    const lastName = identityContext?.lastName ?? items.find((item) => item.key === LAST_NAME)?.value;
    const opportunityId = identityContext?.opportunityId ?? items.find((item) => item.key === OPPORTUNITY_UID)?.value;

    const pivotSourceKeysInOrder = [PIVOT_SOURCE_FROM, PIVOT_SOURCE_AT, PIVOT_SOURCE_OPPORTUNITY_UID, PIVOT_SOURCE_CONVERSATION_UID, PIVOT_SOURCE_REASON];
    const pivotTargetKeysInOrder = [PIVOT_TARGET_TO, PIVOT_TARGET_AT, PIVOT_TARGET_OPPORTUNITY_UID, PIVOT_TARGET_CONVERSATION_UID];

    const isPivotDetailsSection = pivotSourceKeysInOrder.some((k) => items.some(({ key }) => key === k)) || pivotTargetKeysInOrder.some((k) => items.some(({ key }) => key === k));

    function renderValue(value: string | undefined, key: string): ReactNode {

        if (!value) return "-";

        switch (key) {
            case PIVOT_SOURCE_FROM:
                return <LinkShowcase linkUrl={`/insights/moreInfo?opportunityId=${pivotSourceOpportunityUid}`} linkText={value} />

            case PIVOT_TARGET_TO:
                return <LinkShowcase linkUrl={`/insights/moreInfo?opportunityId=${pivotTargetOpportunityUid}`} linkText={value} />
            
            case LAST_EVENT:
                return (
                    <button
                        className={styles.clickableButton}
                        onClick={() => HandleLastEventClick(opportunityId, firstName, lastName)}
                    >
                        {value}
                    </button>
                );

            case AGENT_NAME:
                return (
                    <Link
                        className={styles.clickableButton}
                        to={`${AGENTS_EDIT_ROUTE}?${EDIT_AGENT_QUERY_PARAM}=${userId}`}
                        target='_blank'
                    >
                        {value}
                    </Link>
                )

            case PIVOT_SOURCE_AT:
            case PIVOT_TARGET_AT:
                return formatUTCDateTime(value, false, true);

            case FLAG_PAYMENT_FAILED:
                return <p style={{ color: value ? HIGHLIGHT_COLOR : "#3EAE54" }}>{value ? "True" : "False"}</p>

            case LEAD_STATE_ABBR:
                {
                    const addressStateValue = items.find((item) => item.key === ADDRESS_STATE)?.value;
                    const isSameAddressState = (value === addressStateValue);
                    return <p style={{ color: isSameAddressState ? "#000000" : HIGHLIGHT_COLOR }}>{value}</p>
                }

            case ADDRESS_STATE:
                {
                    const leadStateAbbrValue = items.find((item) => item.key === LEAD_STATE_ABBR)?.value;
                    const isSameLeadStateAbbr = (value === leadStateAbbrValue);
                    return <p style={{ color: isSameLeadStateAbbr ? "#000000" : HIGHLIGHT_COLOR }}>{value}</p>
                }

            case OPPORTUNITY_UID:
                return renderUid(value, "Opportunity UID");

            case CONVERSATION_ID:
                return renderUid(value, "Conversation ID");

            case PIVOT_TARGET_OPPORTUNITY_UID:
                return renderUid(value, "Pivot Target Opportunity UID");

            case PIVOT_SOURCE_OPPORTUNITY_UID:
                return renderUid(value, "Pivot Source Opportunity UID");

            case PIVOT_TARGET_CONVERSATION_UID:
                return renderUid(value, "Pivot Target Conversation UID");

            case PIVOT_SOURCE_CONVERSATION_UID:
                return renderUid(value, "Pivot Source Conversation UID");

            default:
                return renderDefault(value);
        }

    }

    return (
        <Box className={styles.sectionCtn}>
            <p className={styles.sectionTitle}>
                {title}
            </p>

            {isPivotDetailsSection ? (
                <Box className={styles.pivotGroupsCtn}>
                    {pivotIsSourceGroupAvailable && (
                        <Box className={styles.pivotGroupGrid}>
                            {pivotSourceKeysInOrder.filter((key) => !HIDDEN_KEYS.has(key)).map((key) => {
                                const found = items.find((item) => item.key === key);
                                return (
                                    <DetailsItemCard key={key} itemKey={key} label={found?.label ?? APPLICATION_DETAILS_LABEL_MAP[key] ?? key} value={renderValue(found?.value, key)} timeStamp={found?.timeStamp} />
                                );
                            })}
                        </Box>
                    )}

                    {pivotIsTargetGroupAvailable && (
                        <Box className={styles.pivotGroupGrid}>
                            {pivotTargetKeysInOrder.filter((key) => !HIDDEN_KEYS.has(key)).map((key) => {
                                const found = items.find((item) => item.key === key);
                                return (
                                    <DetailsItemCard key={key} itemKey={key} label={found?.label ?? APPLICATION_DETAILS_LABEL_MAP[key] ?? key} value={renderValue(found?.value, key)} timeStamp={found?.timeStamp} />
                                );
                            })}
                        </Box>
                    )}
                </Box>
            ) : (
                <Box className={styles.detailsGrid}>
                    {items.filter(({ key }) => !HIDDEN_KEYS.has(key)).map(({ key, label, value, timeStamp }) => (
                        <DetailsItemCard key={key} itemKey={key} label={label} value={renderValue(value, key)} timeStamp={timeStamp} />
                    )
                    )}
                </Box>
            )}

            {!noDivider && <Divider className={styles.sectionDivider} />}
        </Box>
    );
}
